import type { SxProps, Theme } from '@mui/material';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { type ReactNode, useId } from 'react';
import InfoDialog from '../infoDialog/infoDialog';
import { useTextFieldTokens } from '../textField/textField.tokens';

export const useFieldIds = (id?: string) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return {
    inputId,
    labelId: `${inputId}-label`,
  };
};

type FieldLabelProps = {
  htmlFor?: string;
  id?: string;
  error?: boolean;
  required?: boolean;
  infoTooltip?: string;
  shouldDisplayInDialog?: boolean;
  children?: ReactNode;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
};

export const FieldLabel = ({
  htmlFor,
  id,
  error = false,
  required = false,
  infoTooltip,
  shouldDisplayInDialog = false,
  children,
  sx,
  'data-testid': dataTestId,
}: FieldLabelProps) => {
  const theme = useTheme();
  const tokens = useTextFieldTokens(theme);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <Typography
        component='label'
        htmlFor={htmlFor}
        id={id}
        variant='labelSmall'
        sx={
          {
            mb: children ? '4px' : '0px',
            display: 'flex',
            alignItems: 'center',
            color: error ? tokens.errorColor : tokens.labelColor,
            ...sx,
          } as any
        }
        data-testid={dataTestId}
      >
        {children}
        {required && (
          <span style={{ color: tokens.requiredIndicatorColor }}>*</span>
        )}
        {infoTooltip && shouldDisplayInDialog && (
          <InfoDialog sx={{ display: 'block' }} information={infoTooltip} />
        )}
      </Typography>
    </Box>
  );
};
